import { useQuery } from "react-query";

import { axiosInstance } from "@src/api/axiosConfig";
import { axiosPath } from "@src/api/axiosPath";

import { queryKeys } from "./queryKeys";

/**
 * 홈 「앱 푸시 통계」 — 관리자가 직접 보낸 전체 푸시의 발송·열람 수.
 *
 * v1/notifications/push-groups 는 stat(messageCount·readCount) 을 같이 내려준다.
 * 버전 없는 같은 경로는 그 값을 0 으로 박아서 주므로 반드시 v1 으로 부른다.
 */

// 관리자 발송(topic) · 발송완료(500) 만 센다. 자동 알림·미발송 건은 제외
const TO_TYPE_ADMIN = "topic";
const STATUS_SENT = 500;

export interface HomePushStatRow {
    no: number;
    title: string;
    sentAt: string | null;
    /** 통계 행 생성 전에는 실제 0과 구분한다. */
    isCollecting: boolean;
    /** 보낸 수 */
    messageCount: number;
    /** 읽은 수 */
    readCount: number;
    /** 읽음률(%) — 서버가 주지 않아 여기서 계산한다 */
    readRate: number;
}

export interface HomePushStatData {
    rows: HomePushStatRow[];
    /** 발송완료된 관리자 푸시 전체 건수 */
    totalPushCount: number;
}

export const toRow = (raw: any): HomePushStatRow => {
    const messageCount = Number(raw?.stat?.messageCount ?? 0);
    const readCount = Number(raw?.stat?.readCount ?? 0);

    return {
        no: Number(raw?.no ?? 0),
        title: String(raw?.msgTitle ?? ""),
        sentAt: raw?.sentAt ?? raw?.reservationAt ?? null,
        isCollecting: raw?.stat == null,
        messageCount,
        readCount,
        readRate: messageCount > 0 ? (readCount / messageCount) * 100 : 0,
    };
};

const fetchData = async (limit: number): Promise<HomePushStatData> => {
    const response = await axiosInstance.get(
        `${axiosPath.nestNotificationsV1}/push-groups`,
        {
            params: {
                page: 1,
                limit,
                orderColumn: "common.reservationAt",
                orderType: "DESC",
                toTypes: [TO_TYPE_ADMIN],
                statuses: [STATUS_SENT],
            },
        },
    );

    const list = response?.data?.list ?? [];

    return {
        rows: list.map(toRow),
        totalPushCount: Number(response?.data?.totalCount ?? 0),
    };
};

export const useHomePushStat = (limit = 8) =>
    useQuery<HomePushStatData, Error>(
        [queryKeys.HOME_PUSH_STAT, limit],
        () => fetchData(limit),
        { refetchOnWindowFocus: true, refetchInterval: 10000, refetchIntervalInBackground: false },
    );
